import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback translations (used while DB loads)
const FALLBACK = {
  "common.loading": "Laden...",
  "nav.login": "Inloggen",
  "nav.register": "Registreren",
  "hero.cta": "Start Gratis Proefperiode",
};

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(() => localStorage.getItem("sp_language") || "nl");
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState(["nl", "en", "fr"]);
  const [loaded, setLoaded] = useState(false);

  // Load translations from database on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/translations/all`)
      .then((res) => {
        setTranslations(res.data.translations || {});
        if (res.data.languages?.length > 0) {
          setLanguages(res.data.languages);
        }
        setLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load translations:", err);
        setLoaded(true); // Continue with fallbacks
      });
  }, []);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem("sp_language", lang);
  }, []);

  const t = useCallback(
    (key) => {
      // Try current language from DB
      const val = translations[language]?.[key];
      if (val) return val;
      // Fallback to NL from DB
      const nlVal = translations.nl?.[key];
      if (nlVal) return nlVal;
      // Fallback to hardcoded
      return FALLBACK[key] || key;
    },
    [language, translations]
  );

  if (!loaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse-slow text-lg">Laden...</div>
      </div>
    );
  }

  return (
    <I18nContext.Provider value={{ language, changeLanguage, t, languages, loaded }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
}
